import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import BlueprintTakeoffPage from "./pages/BlueprintTakeoffPage";
import AdminDashboard from "./pages/AdminDashboard";
import SalesPipelinePage from "./pages/SalesPipelinePage";
import {
  Home, FolderKanban, Users, ClipboardList, ShoppingCart, Clock,
  Calculator, CreditCard, FileText, BarChart3, Settings,
  LogOut, Menu, X, ChevronRight, AlertTriangle, CheckCircle, Circle,
  HardHat, Send, Shield, ClipboardCheck, Calendar, Lock, Receipt, Building2, Landmark, Target
} from "lucide-react";
import OverwatchHUD from "./components/OverwatchHUD";
import GlobalRedAlert from "./components/GlobalRedAlert";
import { PermissionsProvider, usePermissions, NAV_PERMISSION_MAP } from "./contexts/PermissionsContext";
import "@/App.css";

// API Setup + Build Fingerprint
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = "auth_token";

// Build fingerprint: prefer explicit env, otherwise fall back to version + build time
const BUILD_ID =
  process.env.REACT_APP_BUILD_ID ||
  `${process.env.REACT_APP_BUILD_VERSION || "0.1.0"}-${process.env.REACT_APP_BUILD_TIME || "dev"}`;

if (typeof window !== "undefined") {
  try {
    const lastId = window.localStorage.getItem("__build_id_last__");
    if (lastId && lastId !== BUILD_ID) {
      // Stale bundle or hot-reload mismatch
      console.warn("[Build] Build ID changed between loads:", lastId, "->", BUILD_ID);
    }
    window.localStorage.setItem("__build_id_last__", BUILD_ID);
  } catch {
    // Ignore storage failures
  }
  console.info("Build:", BUILD_ID);
}

const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 600000, // 10 minute timeout for comprehensive blueprint analysis
});

// Attach bearer token from localStorage as a fallback when cookies are blocked (e.g., iframe preview)
if (typeof window !== "undefined") {
  API.interceptors.request.use((config) => {
    try {
      const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) {
        if (!config.headers) config.headers = {};
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch {
      // ignore storage errors
    }
    return config;
  });

  // 401 recovery interceptor - single retry with bearer, then clear token
  API.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      try {
        const hasToken = !!window.localStorage.getItem(AUTH_TOKEN_KEY);
        const status = error?.response?.status;

        if (status === 401 && hasToken && !originalRequest?.__retried) {
          originalRequest.__retried = true;
          // Clear Authorization so request interceptor can re-attach from latest token
          if (originalRequest.headers && originalRequest.headers["Authorization"]) {
            delete originalRequest.headers["Authorization"];
          }
          return API(originalRequest);
        }

        if (status === 401 && hasToken) {
          // Second failure with token present  clear it for safety
          window.localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } catch {
        // ignore storage errors
      }

      return Promise.reject(error);
    }
  );
}

// Context
const AuthContext = React.createContext(null);

const useAuth = () => React.useContext(AuthContext);

// Impersonation Banner Component
const ImpersonationBanner = ({ impersonationInfo, onExit }) => {
  const [exiting, setExiting] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [duration, setDuration] = useState(0);
  
  useEffect(() => {
    const startedAt = impersonationInfo?.started_at ? new Date(impersonationInfo.started_at) : new Date();
    
    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now - startedAt) / 1000 / 60);
      setDuration(diff);
    }, 60000);
    
    // Initial calculation
    const now = new Date();
    setDuration(Math.floor((now - startedAt) / 1000 / 60));
    
    return () => clearInterval(interval);
  }, [impersonationInfo?.started_at]);

  const handleExit = async () => {
    setExiting(true);
    try {
      const adminToken = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/end-impersonation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ resolution_notes: resolutionNotes })
      });
      
      if (response.ok) {
        // Clear the impersonation token
        localStorage.removeItem('auth_token');
        localStorage.removeItem('impersonation_session_id');
        
        // Redirect back to admin panel
        window.location.href = '/admin/customers';
      } else {
        console.error('Failed to end impersonation');
      }
    } catch (error) {
      console.error('Error ending impersonation:', error);
    } finally {
      setExiting(false);
    }
  };
  
  return (
    <>
      <div 
        className="fixed top-0 left-0 right-0 z-[9999] bg-red-900 border-b-2 border-red-600"
        style={{ paddingTop: '12px', paddingBottom: '12px', paddingLeft: '24px', paddingRight: '24px' }}
      >
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4">
            <span className="text-red-400 text-xl">▲</span>
            <div>
              <div className="text-white font-bold tracking-wider text-sm">
                SUPER ADMIN MODE
              </div>
              <div className="text-red-200 text-xs">
                Viewing as: {impersonationInfo?.impersonated_by_name || 'Admin'} • Session: {duration} min
              </div>
              <div className="text-red-300 text-xs">
                Reason: {impersonationInfo?.reason || 'Support access'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowExitModal(true)}
            disabled={exiting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded font-semibold text-sm hover:bg-red-500 transition-colors disabled:opacity-50"
          >
            {exiting ? 'Exiting...' : 'EXIT TO ADMIN →'}
          </button>
        </div>
      </div>
      
      {/* Exit Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full border border-slate-700 shadow-2xl">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">End Impersonation Session</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-slate-300">Add resolution notes (optional):</p>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="E.g., Issue was caused by browser cache. Advised customer to clear cache."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                rows={3}
              />
            </div>
            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowExitModal(false)}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleExit}
                disabled={exiting}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50"
              >
                {exiting ? 'Ending...' : 'Return to Admin Panel →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get session token from localStorage
  const getSessionToken = () => {
    try {
      return window.localStorage.getItem(AUTH_TOKEN_KEY) || null;
    } catch {
      return null;
    }
  };

  const checkAuth = async () => {
    try {
      const response = await API.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } catch {
        // ignore storage errors
      }
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, sessionToken: getSessionToken() }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Try to get session_id from hash first, then query params
      let sessionId = null;
      
      // Check hash (e.g., #session_id=xxx)
      if (location.hash?.includes("session_id=")) {
        sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
      }
      
      // Check query params (e.g., ?session_id=xxx)
      if (!sessionId && location.search?.includes("session_id=")) {
        const params = new URLSearchParams(location.search);
        sessionId = params.get("session_id");
      }

      console.log("[OAuth Debug] Hash:", location.hash);
      console.log("[OAuth Debug] Search:", location.search);
      console.log("[OAuth Debug] Extracted sessionId:", sessionId ? sessionId.substring(0, 20) + "..." : "null");

      if (!sessionId) {
        console.error("[OAuth Debug] No session_id found in URL");
        toast.error("Authentication failed - no session found");
        navigate("/login");
        return;
      }

      try {
        console.log("[OAuth Debug] Calling /auth/session endpoint...");
        const response = await API.post("/auth/session", { session_id: sessionId });
        console.log("[OAuth Debug] Session exchange successful");
        setUser(response.data);
        navigate("/dashboard", { replace: true });
      } catch (error) {
        console.error("[OAuth Debug] Auth error:", error);
        toast.error("Authentication failed - " + (error.response?.data?.detail || "unknown error"));
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mx-auto mb-4"></div>
        <p className="text-slate-600 font-ibm">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { state: { from: location } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return user ? children : null;
};

// Sidebar Navigation - TACTICAL SCHEME with Permission Requirements
const navItems = [
  { path: "/dashboard", icon: Home, label: "Command Center", permission: "view_dashboard" },
  { path: "/leads", icon: Users, label: "Lead Command Center", permission: "view_leads" },
  { path: "/appointments", icon: Calendar, label: "Calendar", permission: "view_calendar" },
  { path: "/calendar/field-visit", icon: Calendar, label: "Field Visit", permission: "view_field_visit" },
  { path: "/estimating", icon: Calculator, label: "Mission Scope", permission: "view_estimating" },
  { path: "/blueprint-takeoff", icon: FileText, label: "Blueprint Takeoff", permission: "view_blueprint_takeoff" },
  { path: "/sales-pipeline", icon: Target, label: "Sales Pipeline", permission: "view_estimates" },
  { path: "/projects", icon: FolderKanban, label: "Active Operations", permission: "view_projects" },
  { path: "/subcontractors", icon: HardHat, label: "Subcontractors", permission: "view_subcontractors" },
  { path: "/company-operations", icon: Building2, label: "Company Operations", permission: "view_expenses" },
  { path: "/settings", icon: Settings, label: "Settings", permission: "view_settings" },
];

// Main Layout with Permission-Based Navigation
const MainLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { can, userRole, loading: permissionsLoading } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hudOpen, setHudOpen] = useState(false);
  
  // Check if in impersonation mode
  const isImpersonation = user?.is_impersonation || false;
  const impersonationInfo = user?.impersonation_info || null;
  
  // Filter nav items based on user permissions
  const visibleNavItems = navItems.filter(item => {
    // Always show dashboard
    if (item.path === '/dashboard') return true;
    // Check permission
    return can(item.permission);
  });

  return (
    <div className="flex h-screen min-h-screen overflow-hidden bg-slate-950">
      {/* Global Red Alert for New Leads */}
      <GlobalRedAlert userId={user?.user_id || 'global-alerts'} />
      
      {/* Impersonation Banner - Fixed at top */}
      {isImpersonation && (
        <ImpersonationBanner 
          impersonationInfo={impersonationInfo}
          onExit={() => {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('impersonation_session_id');
            window.location.href = '/admin/customers';
          }}
        />
      )}
      
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Tactical Dark - use lower z-index to not block modals */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-60 bg-slate-900 border-r border-slate-800 flex flex-col overflow-hidden transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{ 
          height: '100vh',
          zIndex: 40, // Lower than modals (11000+) but enough for mobile overlay
          marginTop: isImpersonation ? '80px' : '0', // Offset for impersonation banner
        }}
      >
        {/* Logo - fixed header */}
        <div className="flex-shrink-0 p-6 border-b border-slate-800">
          <h1 className="text-lg font-chivo font-bold text-white tracking-tight">
            CONTRACTOR<span className="text-emerald-500"> OVERWATCH</span>
          </h1>
          <p className="text-[10px] text-slate-500 mt-1 font-ibm tracking-widest uppercase">Tactical Operations Command</p>
        </div>

        {/* Nav Items - ONLY THIS SCROLLS - Permission Filtered */}
        <nav className="flex-1 min-h-0 p-4 overflow-y-auto overflow-x-hidden">
          <ul className="space-y-1">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-md font-ibm text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-emerald-500/10 text-emerald-400 font-semibold border-l-2 border-emerald-500"
                        : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                    }`}
                  >
                    {Icon && <Icon size={18} />}
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
          
          {/* Show role badge at bottom of nav */}
          {userRole && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50">
                <Shield size={14} className="text-emerald-500" />
                <span className="text-xs text-slate-400">Role:</span>
                <span className="text-xs font-semibold text-emerald-400 uppercase">{userRole.replace(/_/g, ' ')}</span>
              </div>
            </div>
          )}
        </nav>

        {/* User Info + Sign Out - ALWAYS VISIBLE (fixed footer) */}
        <div className="flex-shrink-0 p-4 border-t border-slate-800 bg-slate-900">
          {/* VETERAN OWNED Badge */}
          <div className="flex items-center justify-center gap-1.5 mb-3 py-1.5 px-2 rounded bg-slate-800/50 border border-slate-700/50">
            <span className="text-sm">🇺🇸</span>
            <span className="text-[10px] font-semibold text-slate-400 tracking-wider">VETERAN OWNED</span>
          </div>
          
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content - Tactical Dark */}
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-950" style={{ height: '100vh' }}>
        {/* Desktop Header with OVERWATCH button */}
        <header className="hidden lg:flex flex-shrink-0 bg-slate-900 border-b border-slate-800 px-6 py-3 items-center justify-end">
          <button
            onClick={() => setHudOpen(!hudOpen)}
            data-testid="overwatch-toggle-btn"
            className={`flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-md transition-all duration-300 ${
              hudOpen ? "ring-2 ring-emerald-400 ring-offset-2 ring-offset-slate-900" : "hover:scale-105"
            } animate-pulse-subtle`}
          >
            <Shield size={18} className={hudOpen ? "animate-pulse" : ""} />
            <span className="tracking-wide">OVERWATCH</span>
          </button>
        </header>

        {/* Mobile Header */}
        <header className="lg:hidden flex-shrink-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
              className="p-2 hover:bg-slate-800 rounded-md"
            >
              <Menu size={24} className="text-slate-300" />
            </button>
            <h1 className="text-lg font-chivo font-bold text-white">
              CONTRACTOR<span className="text-emerald-500"> OVERWATCH</span>
            </h1>
          </div>
          <button
            onClick={() => setHudOpen(!hudOpen)}
            data-testid="overwatch-toggle-btn-mobile"
            className={`flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-md transition-all duration-300 ${
              hudOpen ? "ring-2 ring-emerald-400" : ""
            }`}
          >
            <Shield size={16} />
            <span className="hidden sm:inline tracking-wide">OVERWATCH</span>
          </button>
        </header>

        {/* Page Content - Tactical Dark Theme */}
        <main 
          className="flex-1 min-h-0 overflow-hidden bg-slate-950"
          style={{ marginTop: isImpersonation ? '80px' : '0' }}
        >
          <div className="h-full overflow-y-auto p-6">
            {children}
          </div>
        </main>
      </div>

      {/* OVERWATCH HUD - Floating Overlay */}
      {hudOpen && (
        <OverwatchHUD 
          estimateData={null}
          onAddToEstimate={() => {}}
          onClose={() => setHudOpen(false)}
        />
      )}
    </div>
  );
};

// Login Page - CONTRACTOR OVERWATCH Final Visual Deployment
const LoginPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [whoamiStatus, setWhoamiStatus] = useState(null);
  const [whoamiBody, setWhoamiBody] = useState(null);

  const isIframe = typeof window !== "undefined" && window.self !== window.top;

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("diagnostics") === "1") {
      setShowDiagnostics(true);
      runDiagnostics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const runDiagnostics = async () => {
    try {
      const resp = await API.get("/auth/me");
      setWhoamiStatus(resp.status);
      setWhoamiBody(resp.data);
    } catch (err) {
      const status = err.response?.status ?? null;
      setWhoamiStatus(status);
      setWhoamiBody({ detail: err.response?.data?.detail || err.message || "error" });
    }
  };

  const authModeLabel = React.useMemo(() => {
    if (typeof window === "undefined") return "unknown";
    try {
      const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
      return token ? "bearer" : "cookie-only/none";
    } catch {
      return "unknown";
    }
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await API.post("/auth/login", { email, password });
      setUser(response.data);

      // Store bearer token fallback for iframe/preview environments
      if (response.data?.session_token) {
        try {
          window.localStorage.setItem(AUTH_TOKEN_KEY, response.data.session_token);
        } catch {
          // ignore storage errors
        }
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 500) {
        try {
          await API.post("/auth/logout");
        } catch (_) {}
      }
      setError(err.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950">
      {/* PERMANENT LOGIN BACKGROUND - HARDCODED URL - DO NOT MODIFY */}
      <div 
        className="absolute inset-0 login-background-permanent"
        style={{
          /* HARDCODED BACKUP - This URL must NEVER be changed or removed */
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_estimator-hud/artifacts/ykx4wycb_image.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />

      {/* Functional Login Form - Positioned to overlay the image's login card */}
      <div className="relative z-10 min-h-screen flex items-center justify-end pr-4 sm:pr-8 lg:pr-16 xl:pr-24">
        <div className="w-full max-w-sm lg:max-w-md" style={{ marginRight: '2%' }}>
          {/* Transparent functional form that sits over the image */}
          <form onSubmit={handleEmailLogin} className="space-y-4 pt-32 lg:pt-40">
            {error && (
              <div className="p-3 bg-red-900/80 border border-red-700 rounded-md">
                <p className="text-sm text-red-200">{error}</p>
              </div>
            )}

            {/* Google Login - Positioned over image's Google button area */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
              className="w-full flex items-center justify-center gap-3 bg-white/95 text-slate-800 font-medium py-3 px-4 rounded-md hover:bg-white transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-500/50"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-slate-800/80 text-slate-400 text-xs rounded">or login with email</span>
              </div>
            </div>

            {/* Email Input */}
      {/* Build / Diagnostics strip */}
      <div className="absolute bottom-2 right-3 text-[10px] text-slate-400 flex items-center gap-3">
        <span>Build: {BUILD_ID}</span>
        <button
          type="button"
          onClick={() => {
            setShowDiagnostics((prev) => !prev);
            if (!whoamiStatus) {
              runDiagnostics();
            }
          }}
          className="underline hover:text-slate-200"
        >
          Diagnostics
        </button>
      </div>

      {showDiagnostics && (
        <div className="absolute bottom-8 right-3 w-80 max-w-full bg-slate-900/95 border border-slate-700 rounded-md p-3 text-[11px] text-slate-100 shadow-lg space-y-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold">Auth Diagnostics</span>
            <button
              type="button"
              onClick={() => setShowDiagnostics(false)}
              className="text-slate-400 hover:text-slate-200"
            >
              ✕
            </button>
          </div>
          <div>API base: {BACKEND_URL || "(missing)"}</div>
          <div>Origin: {typeof window !== "undefined" ? window.location.origin : "n/a"}</div>
          <div>In iframe: {isIframe ? "yes" : "no"}</div>
          <div>Cookies enabled: {typeof navigator !== "undefined" && navigator.cookieEnabled ? "yes" : "no"}</div>
          <div>
            Token present: {typeof window !== "undefined" && !!window.localStorage.getItem(AUTH_TOKEN_KEY) ? "yes" : "no"}
          </div>
          <div>Auth mode: {authModeLabel}</div>
          <div>whoami status: {whoamiStatus ?? "(not called)"}</div>
          <div className="truncate">
            whoami detail: {whoamiBody?.detail || (whoamiBody ? JSON.stringify(whoamiBody) : "-")}
          </div>
          {whoamiStatus === 401 && (
            <div className="mt-1 text-amber-300">
              Hint: Likely third-party cookies blocked in preview. Use token fallback.
            </div>
          )}
          <button
            type="button"
            onClick={runDiagnostics}
            className="mt-1 text-xs underline text-slate-300 hover:text-white"
          >
            Re-run whoami
          </button>
        </div>
      )}

            <div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="w-full px-4 py-3 bg-white/95 border border-slate-300 rounded-md text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="you@company.com"
              />
            </div>

            {/* Password Input */}
            <div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="password-input"
                className="w-full px-4 py-3 bg-white/95 border border-slate-300 rounded-md text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="email-login-btn"
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold py-3.5 px-4 rounded-md hover:bg-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
          
          {/* Demo Credentials */}
          <div className="mt-4 p-2 bg-slate-900/80 border border-slate-700 rounded-lg text-center">
            <p className="text-xs text-slate-300">
              <span className="text-emerald-400 font-semibold">Demo:</span> demo@buildtrackpm.com / demo123
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingExpanded, setBriefingExpanded] = useState(false);
  const [highlightIntel, setHighlightIntel] = useState(false);
  const [deltas, setDeltas] = useState(null);
  const [commanderNotes, setCommanderNotes] = useState("");

  // Helper to derive simple operational signals when activity feed is missing
  const getOperationalSignals = () => {
    if (!data) return [];
    const signals = [];
    if (typeof data.unpaid_total === "number" && data.unpaid_total > 0) {
      signals.push({
        id: "receivables",
        label: `Receivables outstanding: $${data.unpaid_total.toLocaleString()}`,
        route: "/payments",
      });
    }
    if (typeof data.projects_behind_schedule === "number" && data.projects_behind_schedule > 0) {
      signals.push({
        id: "behind",
        label: `Behind schedule: ${data.projects_behind_schedule} project${data.projects_behind_schedule === 1 ? "" : "s"}`,
        route: "/projects",
      });
    }
    if (typeof data.open_issues === "number" && data.open_issues > 0) {
      signals.push({
        id: "issues",
        label: `Open issues: ${data.open_issues}`,
        route: "/walkthroughs",
      });
    }
    if (typeof data.open_pos === "number" && data.open_pos > 0) {
      signals.push({
        id: "pos",
        label: `Open POs: ${data.open_pos}`,
        route: "/purchase-orders",
      });
    }
    if (typeof data.today_time_entries === "number" && data.today_time_entries > 0) {
      const hrs = typeof data.today_hours === "number" ? data.today_hours : 0;
      signals.push({
        id: "time",
        label: `Time entries today: ${data.today_time_entries} (${hrs} hrs)`,
        route: "/time-tracking",
      });
    }
    return signals;
  };

  // Function to highlight intelligence keywords in briefing text
  const highlightBriefing = (text) => {
    if (!text) return text;
    
    const keywords = [
      'overdue', 'blocked', 'unassigned', 'risk', 'deadline', '$'
    ];
    const parts = [];
    let remaining = text;

    while (remaining.length > 0) {
      let earliestIndex = -1;
      let earliestKeyword = null;

      for (const kw of keywords) {
        const idx = remaining.toLowerCase().indexOf(kw.toLowerCase());
        if (idx !== -1 && (earliestIndex === -1 || idx < earliestIndex)) {
          earliestIndex = idx;
          earliestKeyword = kw;
        }
      }

      if (earliestIndex === -1 || !earliestKeyword) {
        parts.push(remaining);
        break;
      }

      if (earliestIndex > 0) {
        parts.push(remaining.slice(0, earliestIndex));
      }

      const matchText = remaining.slice(earliestIndex, earliestIndex + earliestKeyword.length);
      parts.push(
        <span key={parts.length} className="bg-yellow-300 text-slate-900 px-0.5 rounded">
          {matchText}
        </span>
      );

      remaining = remaining.slice(earliestIndex + earliestKeyword.length);
    }

    return parts;
  };

  useEffect(() => {
    const storedNotes = window.localStorage.getItem("commander_notes_v1");
    if (storedNotes != null) {
      setCommanderNotes(storedNotes);
    }

    const fetchDashboard = async () => {
      try {
        const response = await API.get("/dashboard");
        const dashboardData = response.data;
        setData(dashboardData);

        try {
          const raw = window.localStorage.getItem("dash_snapshot_v1");
          if (raw) {
            try {
              const prev = JSON.parse(raw) || {};
              const current = {
                active_projects: dashboardData.active_projects || 0,
                projects_behind_schedule: dashboardData.projects_behind_schedule || 0,
                open_issues: dashboardData.open_issues || 0,
                open_pos: dashboardData.open_pos || 0,
                unpaid_total: dashboardData.unpaid_total || 0,
                today_time_entries: dashboardData.today_time_entries || 0,
                today_hours: dashboardData.today_hours || 0,
              };
              setDeltas({
                active_projects: current.active_projects - (prev.active_projects || 0),
                projects_behind_schedule: current.projects_behind_schedule - (prev.projects_behind_schedule || 0),
                open_issues: current.open_issues - (prev.open_issues || 0),
                open_pos: current.open_pos - (prev.open_pos || 0),
                unpaid_total: current.unpaid_total - (prev.unpaid_total || 0),
                today_time_entries: current.today_time_entries - (prev.today_time_entries || 0),
                today_hours: current.today_hours - (prev.today_hours || 0),
              });
              window.localStorage.setItem("dash_snapshot_v1", JSON.stringify(current));
            } catch (e) {
              // reset snapshot if parse fails
              window.localStorage.removeItem("dash_snapshot_v1");
            }
          } else {
            const initial = {
              active_projects: dashboardData.active_projects || 0,
              projects_behind_schedule: dashboardData.projects_behind_schedule || 0,
              open_issues: dashboardData.open_issues || 0,
              open_pos: dashboardData.open_pos || 0,
              unpaid_total: dashboardData.unpaid_total || 0,
              today_time_entries: dashboardData.today_time_entries || 0,
              today_hours: dashboardData.today_hours || 0,
            };
            window.localStorage.setItem("dash_snapshot_v1", JSON.stringify(initial));
          }
        } catch {
          // localStorage may be unavailable (e.g., private mode); ignore
        }
      } catch (error) {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const loadBriefing = async () => {
    setBriefingLoading(true);
    try {
      // Soft tone influence: pass a sanitized leadership context excerpt as a hint only
      const notesText = (commanderNotes || "").trim();
      let leadershipContext = null;
      if (notesText) {
        // Strip bullets and collapse whitespace
        let cleaned = notesText.replace(/^[\u2022\-\*]+/gm, "").replace(/\s+/g, " ").trim();
        if (cleaned.length > 300) {
          cleaned = cleaned.slice(0, 300);
        }
        if (cleaned) {
          leadershipContext = cleaned;
        }
      }

      const params = leadershipContext ? { leadership_context: leadershipContext } : {};
      const response = await API.get("/ai-coo/daily-briefing", { params });
      setBriefing(response.data);
      setBriefingExpanded(false);
    } catch (error) {
      toast.error("Failed to load AI briefing");
    } finally {
      setBriefingLoading(false);
    }
  };

  const copyBriefing = async () => {
    if (!briefing?.briefing) return;
    const text = briefing.briefing;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Briefing copied to clipboard");
      } else {
        // Fallback: temporary textarea
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          const successful = document.execCommand("copy");
          if (successful) {
            toast.success("Briefing copied to clipboard");
          } else {
            toast.error("Failed to copy briefing");
          }
        } finally {
          document.body.removeChild(textarea);
        }
      }
    } catch (err) {
      toast.error("Failed to copy briefing");
    }
  };

  if (loading) {
    return <div className="animate-pulse text-slate-400">Loading dashboard...</div>;
  }

  // Tactical StatCard Component
  const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-slate-900 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-ibm text-slate-500 mb-1 uppercase tracking-wide">{title}</p>
          <p className="text-3xl font-chivo font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </button>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">COMMAND CENTER</h1>
          <p className="text-slate-500 font-ibm mt-1">Welcome back! Here&apos;s your operations overview.</p>
        </div>
      </div>

      {/* ALERT STRIP */}
      {data && (
        <div className="flex flex-wrap gap-2 mb-4">
          {data.unpaid_total > 0 && (
            <button
              onClick={() => navigate("/payments")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/40 text-xs font-semibold"
            >
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              Receivables Outstanding: ${data.unpaid_total.toLocaleString()}
            </button>
          )}
          {data.projects_behind_schedule > 0 && (
            <button
              onClick={() => navigate("/projects")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/40 text-xs font-semibold"
            >
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              Projects Behind Schedule: {data.projects_behind_schedule}
            </button>
          )}
          {data.open_issues > 0 && (
            <button
              onClick={() => navigate("/walkthroughs")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/40 text-xs font-semibold"
            >
              <span className="w-2 h-2 rounded-full bg-orange-400" />
              Open Issues: {data.open_issues}
            </button>
          )}
          {data.open_pos > 0 && (
            <button
              onClick={() => navigate("/purchase-orders")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/40 text-xs font-semibold"
            >
              <span className="w-2 h-2 rounded-full bg-sky-400" />
              Open POs: {data.open_pos}
            </button>
          )}
        </div>
      )}

      {/* COMMANDER'S NOTES - Human-authored intent */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs font-ibm text-slate-500 tracking-wide uppercase">Commander's Notes</p>
            <p className="text-[11px] text-slate-500">Human-authored priorities and intent.</p>
          </div>
        </div>
        <div className="space-y-3">
          <textarea
            className="w-full min-h-[96px] max-h-48 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder={"Example:\n• Push Smith project to completion by Friday\n• Call supplier about delayed windows\n• Hold on new starts until receivables clear"}
            value={commanderNotes}
            onChange={(e) => setCommanderNotes(e.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>Local to this browser. Not shared. Not AI.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setCommanderNotes("");
                  window.localStorage.removeItem("commander_notes_v1");
                  toast.success("Commander's notes cleared");
                }}
                className="px-3 py-1.5 rounded-md border border-slate-700 text-[11px] text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                CLEAR
      {/* VARIANCE WATCH - Basic alignment check (stub, deterministic rules only) */}
      {/* NOTE: Full Weekly Intent + Variance engine to be implemented in a later slice.
          This placeholder ensures layout room without changing behavior when unused. */}


              </button>
              <button
                type="button"
                onClick={() => {
                  window.localStorage.setItem("commander_notes_v1", commanderNotes || "");
                  toast.success("Commander's notes saved");
                }}
                className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-[11px] font-semibold text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                SAVE NOTES
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QUICK ACTIONS - Context-Aware */}
      {data && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-ibm text-slate-500 tracking-wide uppercase">Quick Actions</p>
              <p className="text-[11px] text-slate-500">Jump to the highest-impact control panels.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Collect Receivables */}
            <button
              type="button"
              onClick={() => navigate("/payments")}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-left text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <div className="pr-2">
                <p className="font-medium">Collect Receivables</p>
                <p className="text-[11px] text-slate-400">Go to payments and clear A/R.</p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    data.unpaid_total > 0
                      ? "bg-red-500/10 text-red-400 border-red-500/40"
                      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                  }`}
                >
                  {data.unpaid_total > 0
                    ? `$${data.unpaid_total.toLocaleString()} outstanding`
                    : "All clear"}
                </span>
                <ChevronRight size={14} className="text-slate-400" />
              </div>
            </button>

            {/* Stabilize Schedule */}
            <button
              type="button"
              onClick={() => navigate("/projects")}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-left text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <div className="pr-2">
                <p className="font-medium">Stabilize Schedule</p>
                <p className="text-[11px] text-slate-400">Review projects that are slipping.</p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    data.projects_behind_schedule > 0
                      ? "bg-amber-500/10 text-amber-300 border-amber-500/40"
                      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                  }`}
                >
                  {data.projects_behind_schedule > 0
                    ? `${data.projects_behind_schedule} behind`
                    : "On track"}
                </span>
                <ChevronRight size={14} className="text-slate-400" />
              </div>
            </button>

            {/* Resolve Field Issues */}
            <button
              type="button"
              onClick={() => navigate("/walkthroughs")}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-left text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <div className="pr-2">
                <p className="font-medium">Resolve Field Issues</p>
                <p className="text-[11px] text-slate-400">Jump to punch walks and defects.</p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    data.open_issues > 0
                      ? "bg-orange-500/10 text-orange-300 border-orange-500/40"
                      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                  }`}
                >
                  {data.open_issues > 0 ? `${data.open_issues} open` : "No issues"}
                </span>
                <ChevronRight size={14} className="text-slate-400" />
              </div>
            </button>

            {/* Approve Purchase Orders */}
            <button
              type="button"
              onClick={() => navigate("/purchase-orders")}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-left text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <div className="pr-2">
                <p className="font-medium">Approve Purchase Orders</p>
                <p className="text-[11px] text-slate-400">Unblock material and sub flows.</p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    data.open_pos > 0
                      ? "bg-sky-500/10 text-sky-300 border-sky-500/40"
                      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                  }`}
                >
                  {data.open_pos > 0 ? `${data.open_pos} pending` : "No POs"}
                </span>
                <ChevronRight size={14} className="text-slate-400" />
              </div>
            </button>
          </div>
        </div>
      )}

      {/* SALES PIPELINE - Opportunities Tracking */}
      {data?.sales_pipeline && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-ibm text-slate-500 tracking-wide uppercase">Sales Pipeline</p>
              <p className="text-[11px] text-slate-500">Track opportunities from estimate to close.</p>
            </div>
            <button
              onClick={() => navigate("/sales-pipeline")}
              className="text-[10px] text-emerald-400 hover:text-emerald-300"
            >
              View All →
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Estimating */}
            <button
              type="button"
              onClick={() => navigate("/sales-pipeline?stage=estimating")}
              className="flex flex-col items-center justify-center w-full px-3 py-3 rounded-md bg-slate-800 hover:bg-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <span className="text-2xl font-chivo font-bold text-white">{data.sales_pipeline.draft_estimates || 0}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Estimating</span>
              <span className="text-[10px] text-emerald-400">${(data.sales_pipeline.estimates_value || 0).toLocaleString()}</span>
            </button>
            {/* Proposed */}
            <button
              type="button"
              onClick={() => navigate("/sales-pipeline?stage=proposed")}
              className="flex flex-col items-center justify-center w-full px-3 py-3 rounded-md bg-slate-800 hover:bg-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <span className="text-2xl font-chivo font-bold text-white">{data.sales_pipeline.pending_proposals || 0}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Proposed</span>
              <span className="text-[10px] text-amber-400">${(data.sales_pipeline.proposals_value || 0).toLocaleString()}</span>
            </button>
            {/* New Leads */}
            <button
              type="button"
              onClick={() => navigate("/leads")}
              className="flex flex-col items-center justify-center w-full px-3 py-3 rounded-md bg-slate-800 hover:bg-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <span className="text-2xl font-chivo font-bold text-white">{data.sales_pipeline.new_leads}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">New Leads</span>
              <span className="text-[10px] text-sky-400">{data.sales_pipeline.total_leads} total</span>
            </button>
            {/* Today's Appointments */}
            <button
              type="button"
              onClick={() => navigate("/appointments")}
              className="flex flex-col items-center justify-center w-full px-3 py-3 rounded-md bg-slate-800 hover:bg-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <span className="text-2xl font-chivo font-bold text-white">{data.appointments?.today || 0}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Today&apos;s Appts</span>
              <span className="text-[10px] text-purple-400">{data.appointments?.upcoming || 0} upcoming</span>
            </button>
          </div>
          {/* Field Visits Alert */}
          {data.field_visits?.pending_estimate > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/40">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs text-amber-300">
                {data.field_visits.pending_estimate} field visit{data.field_visits.pending_estimate > 1 ? 's' : ''} ready for estimate generation
              </span>
              <button
                onClick={() => navigate("/calendar/field-visit")}
                className="ml-auto text-xs text-amber-400 hover:text-amber-300 underline"
              >
                View
              </button>
            </div>
          )}
        </div>
      )}

      {/* OPPORTUNITIES PIPELINE HEALTH - Enhanced Pipeline Tracking */}
      {data?.opportunities_pipeline && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-ibm text-slate-500 tracking-wide uppercase">Pipeline Health</p>
              <p className="text-[11px] text-slate-500">Track opportunity progress and follow-ups.</p>
            </div>
            <button
              onClick={() => navigate("/sales-pipeline")}
              className="text-[10px] text-emerald-400 hover:text-emerald-300"
            >
              View Pipeline →
            </button>
          </div>
          
          {/* Alert banners for actionable items */}
          <div className="flex flex-wrap gap-2 mb-3">
            {data.opportunities_pipeline.overdue_reminders > 0 && (
              <button
                onClick={() => navigate("/sales-pipeline?filter=overdue")}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/40 text-xs font-semibold"
              >
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {data.opportunities_pipeline.overdue_reminders} Overdue Follow-ups
              </button>
            )}
            {data.opportunities_pipeline.stale_proposals > 0 && (
              <button
                onClick={() => navigate("/sales-pipeline?stage=viewed")}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/40 text-xs font-semibold"
              >
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                {data.opportunities_pipeline.stale_proposals} Stale Proposals
              </button>
            )}
            {data.opportunities_pipeline.pending_reminders > 0 && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/40 text-xs">
                <span className="w-2 h-2 rounded-full bg-sky-400" />
                {data.opportunities_pipeline.pending_reminders} Pending Reminders
              </span>
            )}
          </div>
          
          {/* Pipeline Stage Breakdown */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {[
              { key: "estimating", label: "Estimating", color: "text-slate-400" },
              { key: "pending", label: "Pending", color: "text-sky-400" },
              { key: "proposed", label: "Proposed", color: "text-amber-400" },
              { key: "viewed", label: "Viewed", color: "text-purple-400" },
              { key: "negotiating", label: "Negotiating", color: "text-orange-400" },
              { key: "won", label: "Won", color: "text-emerald-400" },
            ].map(stage => (
              <button
                key={stage.key}
                onClick={() => navigate(`/sales-pipeline?stage=${stage.key}`)}
                className="flex flex-col items-center justify-center px-2 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <span className={`text-lg font-chivo font-bold ${stage.color}`}>
                  {data.opportunities_pipeline.by_stage?.[stage.key] || 0}
                </span>
                <span className="text-[9px] text-slate-500 uppercase tracking-wide">{stage.label}</span>
              </button>
            ))}
          </div>
          
          {/* Pipeline Value Summary */}
          <div className="mt-3 flex items-center justify-between px-3 py-2 rounded-md bg-slate-800/50">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Active Pipeline</p>
                <p className="text-lg font-chivo font-bold text-white">
                  ${(data.opportunities_pipeline.active_pipeline_value || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-px h-8 bg-slate-700" />
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Won This Period</p>
                <p className="text-lg font-chivo font-bold text-emerald-400">
                  ${(data.opportunities_pipeline.won_value || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase">Active Opportunities</p>
              <p className="text-lg font-chivo font-bold text-white">
                {data.opportunities_pipeline.total_active || 0}
              </p>
            </div>
          </div>
          
          {/* Conversion Metrics Row */}
          {data.opportunities_pipeline.conversion_metrics && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              <div className="bg-slate-800/70 rounded-md px-3 py-2 text-center">
                <p className="text-[9px] text-slate-500 uppercase">Win Rate</p>
                <p className={`text-lg font-chivo font-bold ${
                  data.opportunities_pipeline.conversion_metrics.win_rate >= 30 
                    ? 'text-emerald-400' 
                    : data.opportunities_pipeline.conversion_metrics.win_rate >= 15 
                      ? 'text-amber-400' 
                      : 'text-red-400'
                }`}>
                  {data.opportunities_pipeline.conversion_metrics.win_rate}%
                </p>
              </div>
              <div className="bg-slate-800/70 rounded-md px-3 py-2 text-center">
                <p className="text-[9px] text-slate-500 uppercase">View Rate</p>
                <p className="text-lg font-chivo font-bold text-sky-400">
                  {data.opportunities_pipeline.conversion_metrics.proposal_to_view_rate}%
                </p>
              </div>
              <div className="bg-slate-800/70 rounded-md px-3 py-2 text-center">
                <p className="text-[9px] text-slate-500 uppercase">Avg Deal</p>
                <p className="text-lg font-chivo font-bold text-white">
                  ${(data.opportunities_pipeline.conversion_metrics.avg_deal_value || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-slate-800/70 rounded-md px-3 py-2 text-center">
                <p className="text-[9px] text-slate-500 uppercase">Closed</p>
                <p className="text-lg font-chivo font-bold text-white">
                  {data.opportunities_pipeline.conversion_metrics.total_closed || 0}
                  <span className="text-xs text-slate-500 ml-1">
                    ({data.opportunities_pipeline.conversion_metrics.lost_count || 0} lost)
                  </span>
                </p>
              </div>
            </div>
          )}
          
          {/* Pipeline Conversion Funnel */}
          {data.opportunities_pipeline && (
            <div className="mt-4 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
              <p className="text-[10px] text-slate-500 uppercase mb-3 font-semibold">Conversion Funnel</p>
              <div className="relative">
                {/* Funnel visualization */}
                {(() => {
                  const stages = [
                    { key: 'estimating', label: 'Estimating', color: 'bg-slate-500' },
                    { key: 'proposed', label: 'Proposed', color: 'bg-amber-500' },
                    { key: 'viewed', label: 'Viewed', color: 'bg-purple-500' },
                    { key: 'negotiating', label: 'Negotiating', color: 'bg-orange-500' },
                    { key: 'won', label: 'Won', color: 'bg-emerald-500' },
                  ];
                  
                  const counts = stages.map(s => data.opportunities_pipeline.by_stage?.[s.key] || 0);
                  const maxCount = Math.max(...counts, 1);
                  
                  // Calculate conversion rates between stages
                  const rates = [];
                  for (let i = 0; i < counts.length - 1; i++) {
                    const rate = counts[i] > 0 ? Math.round((counts[i + 1] / counts[i]) * 100) : 0;
                    rates.push(rate);
                  }
                  
                  return (
                    <div className="space-y-2">
                      {stages.map((stage, idx) => {
                        const count = counts[idx];
                        const width = maxCount > 0 ? Math.max((count / maxCount) * 100, 8) : 8;
                        const value = data.opportunities_pipeline.value_by_stage?.[stage.key] || 0;
                        
                        return (
                          <div key={stage.key}>
                            <div className="flex items-center gap-3">
                              <div className="w-20 text-right">
                                <span className="text-xs text-slate-400">{stage.label}</span>
                              </div>
                              <div className="flex-1 relative">
                                <div 
                                  className={`h-7 ${stage.color} rounded-r-md transition-all duration-500 flex items-center justify-between px-2`}
                                  style={{ width: `${width}%`, minWidth: '60px' }}
                                >
                                  <span className="text-xs font-bold text-white">{count}</span>
                                  {value > 0 && (
                                    <span className="text-[10px] text-white/80">${(value / 1000).toFixed(0)}k</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Conversion rate arrow between stages */}
                            {idx < stages.length - 1 && (
                              <div className="flex items-center gap-3 py-1">
                                <div className="w-20"></div>
                                <div className="flex items-center gap-2 text-[10px]">
                                  <svg className="w-3 h-3 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14M19 12l-7 7-7-7"/>
                                  </svg>
                                  <span className={`font-semibold ${
                                    rates[idx] >= 50 ? 'text-emerald-400' : 
                                    rates[idx] >= 25 ? 'text-amber-400' : 
                                    rates[idx] > 0 ? 'text-red-400' : 'text-slate-500'
                                  }`}>
                                    {rates[idx]}% conversion
                                  </span>
                                  {rates[idx] < 25 && rates[idx] > 0 && counts[idx] > 2 && (
                                    <span className="text-red-400/70 text-[9px]">← bottleneck</span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                
                {/* Lost deals indicator */}
                {(data.opportunities_pipeline.by_stage?.lost || 0) > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center gap-3">
                    <div className="w-20 text-right">
                      <span className="text-xs text-red-400">Lost</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-5 bg-red-500/30 border border-red-500/50 rounded px-2 flex items-center">
                        <span className="text-xs font-bold text-red-400">{data.opportunities_pipeline.by_stage.lost}</span>
                      </div>
                      <span className="text-[10px] text-slate-500">
                        ${((data.opportunities_pipeline.value_by_stage?.lost || 0) / 1000).toFixed(0)}k lost value
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MORNING SITREP - Tactical Operations Briefing */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-6 text-white">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            </div>
            <div>
              <h2 className="text-lg font-chivo font-bold tracking-wide">MORNING SITREP</h2>
              <p className="text-slate-400 text-sm">Tactical operations summary // AI-powered intel</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setHighlightIntel((prev) => !prev)}
              className={`px-3 py-2 rounded-md text-xs font-bold border transition-colors ${highlightIntel ? "bg-yellow-400 text-slate-900 border-yellow-300" : "bg-slate-900 text-slate-200 border-slate-700"}`}
            >
              {highlightIntel ? "HIGHLIGHT ON" : "HIGHLIGHT"}
            </button>
            <button
              onClick={loadBriefing}
              disabled={briefingLoading}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 rounded-md text-sm font-bold transition-colors disabled:opacity-50 flex items-center gap-2 tracking-wide text-slate-900"
            >
              {briefingLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                COMPILING...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12s2.545-5 7-5c4.454 0 7 5 7 5s-2.546 5-7 5c-4.455 0-7-5-7-5z"/><path d="M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/><path d="M21 17v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2"/><path d="M21 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2"/></svg>
                {briefing ? 'REFRESH SITREP' : 'REQUEST SITREP'}
              </>
            )}
            </button>
            {briefing && (
              <>
                <button
                  onClick={copyBriefing}
                  className="px-3 py-2 bg-slate-900 hover:bg-slate-800 rounded-md text-xs font-bold text-slate-200 border border-slate-700 flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  COPY
                </button>
                <button
                  onClick={() => setBriefingExpanded((prev) => !prev)}
                  className="px-3 py-2 bg-slate-900 hover:bg-slate-800 rounded-md text-xs font-bold text-slate-200 border border-slate-700 flex items-center gap-1"
                >
                  {briefingExpanded ? "COLLAPSE" : "EXPAND"}
                </button>
              </>
            )}
          </div>
        </div>

        {briefing ? (
          <div className="bg-white/10 rounded-lg p-4 backdrop-blur border border-emerald-500/20">
            <div className="prose prose-invert prose-sm max-w-none">
              <div
                className={`whitespace-pre-wrap text-slate-100 leading-relaxed transition-all ${briefingExpanded ? "max-h-[600px]" : "max-h-48"} overflow-y-auto`}
              >
                {highlightIntel ? highlightBriefing(briefing.briefing) : briefing.briefing}
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Intel compiled at {new Date(briefing.generated_at).toLocaleString()}
            </p>
            {briefing.data_summary && (
              <p className="text-[10px] text-slate-500 mt-1">
                Intel inputs — Active: {briefing.data_summary.active_projects ?? 0} · Behind: {briefing.data_summary.projects_behind ?? 0} · Issues: {briefing.data_summary.open_issues ?? 0} · POs: {briefing.data_summary.open_pos ?? 0} · Unpaid: ${briefing.data_summary.unpaid_amount?.toLocaleString?.() ?? (briefing.data_summary.unpaid_amount || 0)} · Pending timesheets: {briefing.data_summary.pending_timesheets ?? 0} · OT hours: {briefing.data_summary.overtime_hours ?? 0}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-white/5 rounded-lg p-6 text-center border border-slate-700/50">
            <p className="text-slate-400 mb-2">Request SITREP to receive tactical operations summary</p>
            <p className="text-xs text-slate-500">OVERWATCH analyzes projects, payments, time entries, and operational status</p>
          </div>
        )}
      </div>

      {/* COMMAND ACTIONS */}
      {data && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mt-4">
          <p className="text-xs font-ibm text-slate-500 mb-2 tracking-wide uppercase">Command Actions</p>
          <div className="space-y-2">
            <button
              onClick={() => navigate("/projects")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Active Operations</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/projects")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Behind Schedule</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/walkthroughs")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Open Issues</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/purchase-orders")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Open Purchase Orders</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/payments")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Unpaid Payments</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/estimating")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Mission Scope</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
            <button
              onClick={() => navigate("/blueprint-takeoff")}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-slate-100"
            >
              <span>Blueprint Takeoff</span>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Projects"
          value={data?.active_projects || 0}
          icon={FolderKanban}
          color="bg-slate-900"
          onClick={() => navigate("/projects")}
          subtitle={deltas && Number.isFinite(deltas.active_projects) && deltas.active_projects !== 0 ? `${deltas.active_projects > 0 ? "+" : ""}${deltas.active_projects} vs last load` : undefined}
        />
        <StatCard
          title="Behind Schedule"
          value={data?.projects_behind_schedule || 0}
          icon={AlertTriangle}
          color="bg-amber-500"
          onClick={() => navigate("/projects")}
          subtitle={deltas && Number.isFinite(deltas.projects_behind_schedule) && deltas.projects_behind_schedule !== 0 ? `${deltas.projects_behind_schedule > 0 ? "+" : ""}${deltas.projects_behind_schedule} vs last load` : undefined}
        />
        <StatCard
          title="Open Issues"
          value={data?.open_issues || 0}
          icon={Circle}
          color="bg-red-500"
          onClick={() => navigate("/walkthroughs")}
          subtitle={deltas && Number.isFinite(deltas.open_issues) && deltas.open_issues !== 0 ? `${deltas.open_issues > 0 ? "+" : ""}${deltas.open_issues} vs last load` : undefined}
        />
        <StatCard
          title="Open POs"
          value={data?.open_pos || 0}
          icon={ShoppingCart}
          color="bg-blue-500"
          onClick={() => navigate("/purchase-orders")}
          subtitle={deltas && Number.isFinite(deltas.open_pos) && deltas.open_pos !== 0 ? `${deltas.open_pos > 0 ? "+" : ""}${deltas.open_pos} vs last load` : undefined}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Unpaid Payments"
          value={data?.unpaid_payments || 0}
          icon={CreditCard}
          color="bg-secondary"
          onClick={() => navigate("/payments")}
          subtitle={deltas && Number.isFinite(deltas.unpaid_total) && deltas.unpaid_total !== 0 ? `${deltas.unpaid_total > 0 ? "+" : ""}$${Math.abs(deltas.unpaid_total).toLocaleString()} vs last load` : `$${(data?.unpaid_total || 0).toLocaleString()} total`}
        />
        <StatCard
          title="Today's Time Entries"
          value={data?.today_time_entries || 0}
          icon={Clock}
          color="bg-emerald-500"
          onClick={() => navigate("/time-tracking")}
          subtitle={deltas && Number.isFinite(deltas.today_hours) && deltas.today_hours !== 0 ? `${deltas.today_hours > 0 ? "+" : ""}${deltas.today_hours} hrs vs last load` : `${data?.today_hours || 0} hours logged`}
        />
        <div className="bg-white border border-slate-200 rounded-lg p-6 card-hover">
          <p className="text-sm font-ibm text-slate-500 mb-2">Profit Snapshot</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Total Budget</span>
              <span className="font-semibold">${(data?.profit_snapshot?.total_budget || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Total Spent</span>
              <span className="font-semibold text-amber-600">${(data?.profit_snapshot?.total_spent || 0).toLocaleString()}</span>
            </div>
            <div className="border-t pt-2 flex justify-between">
              <span className="font-semibold text-slate-900">Gross Profit</span>
              <span className="font-bold text-emerald-600">${(data?.profit_snapshot?.gross_profit || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* RECENT ACTIVITY / OPERATIONAL SIGNALS */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-ibm text-slate-500 tracking-wide uppercase">Recent Activity</p>
            <p className="text-[11px] text-slate-500">Last 24–72h operational events.</p>
          </div>
        </div>

        {Array.isArray(data?.recent_activity) && data.recent_activity.length > 0 ? (
          <div className="space-y-2">
            {data.recent_activity.slice(0, 8).map((event, idx) => {
              const type = event.type || event.category || "event";
              const ts = event.timestamp || event.created_at;
              const summary = event.summary || event.title || event.description || "Activity";
              const when = ts ? new Date(ts).toLocaleString() : "time unknown";

              let route = null;
              if (type.includes("project")) route = "/projects";
              else if (type.includes("issue")) route = "/walkthroughs";
              else if (type.includes("payment") || type.includes("invoice")) route = "/payments";
              else if (type.includes("po") || type.includes("purchase")) route = "/purchase-orders";

              // Pick an icon based on type
              const Icon = type.includes("payment") || type.includes("invoice")
                ? CreditCard
                : type.includes("issue")
                  ? AlertTriangle
                  : type.includes("project")
                    ? FolderKanban
                    : Clock;

              return (
                <div
                  key={event.id || event.event_id || idx}
                  className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md hover:bg-slate-800/60"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                      <Icon size={16} className="text-slate-200" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-100 truncate">{summary}</p>
                      <p className="text-[11px] text-slate-500 truncate">{when}</p>
                    </div>
                  </div>
                  {route && (
                    <button
                      type="button"
                      onClick={() => navigate(route)}
                      className="flex items-center gap-1 px-2 py-1 rounded-md border border-slate-700 text-[11px] text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <span>VIEW</span>
                      <ChevronRight size={12} className="text-slate-400" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-sm text-slate-400 mb-1">No activity data available.</p>
            <p className="text-[11px] text-slate-500 mb-2">Operational Signals derived from live KPIs:</p>
            {getOperationalSignals().length > 0 ? (
              <ul className="space-y-1">
                {getOperationalSignals().map((signal) => (
                  <li key={signal.id} className="flex items-center justify-between gap-2 text-[13px] text-slate-200">
                    <span className="truncate">{signal.label}</span>
                    {signal.route && (
                      <button
                        type="button"
                        onClick={() => navigate(signal.route)}
                        className="px-2 py-0.5 rounded-md border border-slate-700 text-[10px] text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        GO
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-slate-500">No significant signals detected from current KPIs.</p>
            )}
          </div>
        )}
      </div>

      {/* Recent Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-4">Recent Active Projects</h3>
          {data?.recent_projects?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_projects.map((project) => (
                <Link
                  key={project.project_id}
                  to={`/projects/${project.project_id}/home`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-md hover:bg-slate-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-slate-900">{project.name}</p>
                    <p className="text-sm text-slate-500">Budget: ${project.budget?.toLocaleString()}</p>
                  </div>
                  <ChevronRight size={20} className="text-slate-400" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No active projects</p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={20} />
            Projects Behind Schedule
          </h3>
          {data?.behind_schedule_projects?.length > 0 ? (
            <div className="space-y-3">
              {data.behind_schedule_projects.map((project) => (
                <Link
                  key={project.project_id}
                  to={`/projects/${project.project_id}/home`}
                  className="flex items-center justify-between p-3 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors border-l-4 border-amber-500"
                >
                  <div>
                    <p className="font-medium text-slate-900">{project.name}</p>
                    <p className="text-sm text-amber-600">Due: {project.target_finish_date}</p>
                  </div>
                  <ChevronRight size={20} className="text-slate-400" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4 flex items-center justify-center gap-2">
              <CheckCircle className="text-emerald-500" size={20} />
              All projects on track!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Import page components
import CalendarPage from "./pages/CalendarPage";
import FieldVisitMissionBoardPage from "./pages/FieldVisitMissionBoardPage";
import FieldVisitExecutionPage from "./pages/FieldVisitExecutionPage_NEW";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectHomePage from "./pages/ProjectHomePage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ClientsPage from "./pages/ClientsPage";
import WalkthroughsPage from "./pages/WalkthroughsPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import JobCostingPage from "./pages/JobCostingPage";
import PaymentsPage from "./pages/PaymentsPage";
import DocumentsPage from "./pages/DocumentsPage";
import TeamPage from "./pages/TeamPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import LeadsClientsPage from "./pages/LeadsClientsPage";
import LeadCommandCenter from "./pages/LeadCommandCenter";
import EstimatingPage from "./pages/EstimatingPage";
import EstimatesPage from "./pages/EstimatesPage";
import SubcontractorsPage from "./pages/SubcontractorsPage";
import JobOffersPage from "./pages/JobOffersPage";
import ProposalBuilder from "./pages/ProposalBuilder";
import PublicProposal from "./pages/PublicProposal";
import PublicOperatorOrder from "./pages/PublicOperatorOrder";
import PublicClientSOW from "./pages/PublicClientSOW";
import PublicSubcontractorSOW from "./pages/PublicSubcontractorSOW";
import ProposalsPage from "./pages/ProposalsPage";
import SubcontractorLogin from "./pages/SubcontractorLogin";
import SubcontractorPortal from "./pages/SubcontractorPortal";
import MarketingPage from "./pages/MarketingPage";
import AboutPage from "./pages/AboutPage";
import ExpensesPage from "./pages/ExpensesPage";
import CompanyOperations from "./pages/CompanyOperations";
import BankReconciliation from "./pages/BankReconciliation";
import ViewProposal from "./pages/ViewProposal";
import InviteAcceptPage from "./pages/InviteAcceptPage";

// Lightweight feature flag pattern example. Incomplete or experimental flows
// must be guarded by a boolean like this and default to false until ready.
// Example only – do NOT flip this in production without an explicit PR gate.
const ENABLE_EXPERIMENTAL_REPORTS_V2 = false;

// App Router
// NOTE: LoginRoute regression guard: /login must always remain a concrete route here.
// If this component stops defining a /login path, the Jest regression test
// `src/__tests__/loginRouteGuard.test.js` will fail and block merges.
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in hash OR query params (OAuth callback)
  const hasSessionInHash = location.hash?.includes("session_id=");
  const hasSessionInQuery = location.search?.includes("session_id=");
  
  if (hasSessionInHash || hasSessionInQuery) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Marketing Landing Page */}
      <Route path="/marketing" element={<MarketingPage />} />
      {/* Public About Page */}
      <Route path="/about" element={<AboutPage />} />
      
      {/* Public Invite Accept Page */}
      <Route path="/invite/accept" element={<InviteAcceptPage />} />
      
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadCommandCenter />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/blueprint-takeoff"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BlueprintTakeoffPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimating"
        element={
          <ProtectedRoute>
            <EstimatingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EstimatesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-pipeline"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SalesPipelinePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProposalsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CalendarPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route


        path="/projects"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId/home"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:projectId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontractors"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SubcontractorsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-offers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <JobOffersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/walkthroughs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WalkthroughsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PurchaseOrdersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-visit/:field_visit_id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FieldVisitExecutionPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-visit/appointment/:appointmentId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FieldVisitExecutionPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/time-tracking"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TimeTrackingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-costing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <JobCostingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PaymentsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ExpensesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-operations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CompanyOperations />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-reconciliation"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BankReconciliation />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PurchaseOrdersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={<Navigate to="/settings/team/members" replace />}
      />
      <Route
        path="/calendar/field-visit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FieldVisitMissionBoardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReportsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SettingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Proposal Builder - Internal (Protected) */}
      <Route
        path="/proposal-builder/:proposalId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProposalBuilder />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposal-builder/new/:estimateId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProposalBuilder />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Public Proposal Portal - No Auth Required */}
      <Route path="/proposal/:token" element={<PublicProposal />} />
      
      {/* Public Operator Order - No Auth Required */}
      <Route path="/operator-order/:token" element={<PublicOperatorOrder />} />
      
      {/* Public Client SOW - No Auth Required */}
      <Route path="/sow-client/:token" element={<PublicClientSOW />} />
      
      {/* Public Subcontractor SOW - No Auth Required */}
      <Route path="/sow-subcontractor/:token" element={<PublicSubcontractorSOW />} />
      
      {/* Public Proposal Viewer - No Auth Required */}
      <Route path="/view-proposal/:token" element={<ViewProposal />} />
      
      {/* Subcontractor Portal - Separate Auth System */}
      <Route path="/subcontractor-portal/login" element={<SubcontractorLogin />} />
      <Route path="/subcontractor-portal/dashboard" element={<SubcontractorPortal />} />
      <Route path="/subcontractor-portal" element={<SubcontractorLogin />} />
      
      {/* Super Admin Dashboard - Staff Only */}
      <Route path="/admin/*" element={<AdminDashboard />} />
      
      {/* Marketing Landing Page at Root - Public */}
      <Route path="/" element={<MarketingPage />} />
      
      {/* Fallback to Login */}
      <Route path="*" element={<LoginPage />} />
    </Routes>
  );
};

// Wrapper to pass user to PermissionsProvider
const AppWithPermissions = () => {
  const { user } = useAuth();
  return (
    <PermissionsProvider user={user}>
      <AppRouter />
    </PermissionsProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppWithPermissions />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, useAuth };
export { usePermissions } from "./contexts/PermissionsContext";
